import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { CameraIcon, XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { useTheme } from '../../../../context/ThemeContext';
import { FileValidationService } from '../../../../services/fileValidation';
import ProfilePhotoCropper from '../../../common/ProfilePhotoCropper';

const ProfilePictureSection = ({
  profilePicture,
  onUpload,
  onRemove,
  isUploading = false,
  uploadError = null,
  userName = ''
}) => {
  const { isDarkMode } = useTheme();
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [fileToProcess, setFileToProcess] = useState(null);

  // Clear validation messages when upload starts or succeeds
  useEffect(() => {
    if (isUploading) {
      setValidationErrors([]);
      setValidationWarnings([]);
    }
  }, [isUploading]);

  // Clear validation messages when profile picture changes (successful upload)
  useEffect(() => {
    if (profilePicture) {
      setValidationErrors([]);
      setValidationWarnings([]);
    }
  }, [profilePicture]);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    // Clear previous validation messages
    setValidationErrors([]);
    setValidationWarnings([]);

    // Handle rejected files from dropzone
    if (rejectedFiles.length > 0) {
      const rejectedFile = rejectedFiles[0];
      const errors = rejectedFile.errors.map(error => {
        switch (error.code) {
          case 'file-too-large':
            return {
              code: 'FILE_TOO_LARGE',
              message: `File is too large (${FileValidationService.formatFileSize(rejectedFile.file.size)})`,
              actionableAdvice: `Please reduce file size to under ${FileValidationService.formatFileSize(FileValidationService.MAX_FILE_SIZE)}`
            };
          case 'file-invalid-type':
            return {
              code: 'INVALID_FORMAT',
              message: 'File format is not supported',
              actionableAdvice: 'Please use JPEG, PNG, GIF, or WebP format'
            };
          default:
            return {
              code: 'VALIDATION_ERROR',
              message: error.message,
              actionableAdvice: 'Please try a different file'
            };
        }
      });
      setValidationErrors(errors);
      return;
    }

    const file = acceptedFiles[0];
    if (file) {
      // Validate the file using our comprehensive validation service
      const validationResult = await FileValidationService.validateFile(file);
      
      if (!validationResult.isValid) {
        setValidationErrors(validationResult.errors);
        return;
      }

      // Set warnings if any
      if (validationResult.warnings.length > 0) {
        setValidationWarnings(validationResult.warnings);
      }

      // File is valid, show cropping interface
      setFileToProcess(file);
      setShowCropper(true);
    }
  }, [onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDragEnter: () => setIsDragOver(true),
    onDragLeave: () => setIsDragOver(false),
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp']
    },
    multiple: false,
    maxSize: FileValidationService.MAX_FILE_SIZE,
    disabled: isUploading,
    noClick: false,
    noKeyboard: false
  });

  const getInitials = (name) => {
    if (!name) return '?';
    const names = name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const handleCropComplete = useCallback((croppedBlob) => {
    setShowCropper(false);
    setFileToProcess(null);
    
    try {
      if (onUpload && croppedBlob) {
        // Convert blob to file for upload
        const croppedFile = new File([croppedBlob], fileToProcess?.name || 'cropped-profile.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        onUpload(croppedFile);
      }
    } catch (error) {
      console.error('Error processing cropped image:', error);
      setValidationErrors([{
        code: 'CROP_ERROR',
        message: 'Failed to process cropped image',
        actionableAdvice: 'Please try uploading your photo again'
      }]);
    }
  }, [onUpload, fileToProcess]);

  const handleCropCancel = useCallback(() => {
    setShowCropper(false);
    setFileToProcess(null);
    // Clear any existing errors when canceling
    setValidationErrors([]);
    setValidationWarnings([]);
  }, []);

  return (
    <div id="profile-picture-section" className={`mb-8 p-6 rounded-lg transition-all duration-300 ${!profilePicture ? (isDarkMode ? 'border-2 border-purple-500/50 ring-2 ring-purple-500/20' : 'border-2 border-purple-500/50 ring-2 ring-purple-500/10') : ''}`}>
      
      {/* Completion Indicator */}
      {!profilePicture && (
        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-purple-900/20 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'}`}>
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                Add a profile picture to complete your profile
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'} mt-1`}>
                A professional photo helps others recognize and connect with you
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Profile Picture Display */}
      <div className="flex flex-col items-center">
        <div className="relative group">
          {profilePicture ? (
            <img
              src={profilePicture}
              alt="Profile"
              className="w-28 h-28 sm:w-36 sm:h-36 rounded-full object-cover shadow-lg ring-4 ring-white dark:ring-gray-800"
            />
          ) : (
            <div className={`
              w-28 h-28 sm:w-36 sm:h-36 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-semibold shadow-lg ring-4 ring-white dark:ring-gray-800
              ${isDarkMode ? 'bg-gradient-to-br from-gray-700 to-gray-800 text-gray-300' : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600'}
            `}>
              {getInitials(userName)}
            </div>
          )}
          
          {/* Remove button - X in corner */}
          {profilePicture && !isUploading && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className={`
                absolute -top-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 z-10
                ${isDarkMode 
                  ? 'bg-gray-600 hover:bg-gray-700 text-white shadow-lg' 
                  : 'bg-gray-500 hover:bg-gray-600 text-white shadow-lg'
                }
                hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
              `}
              title="Remove photo"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
          
          {/* Upload overlay with improved mobile interaction */}
          <div
            {...getRootProps()}
            className={`
              absolute inset-0 rounded-full flex items-center justify-center transition-all duration-300 ease-out
              ${isDragActive 
                ? 'bg-purple-500 bg-opacity-90 scale-105' 
                : 'bg-black bg-opacity-0 hover:bg-opacity-50 group-hover:bg-opacity-50'
              }
              ${isUploading ? 'cursor-not-allowed bg-black bg-opacity-50' : 'cursor-pointer'}
              focus:outline-none focus:ring-4 focus:ring-purple-500 focus:ring-opacity-50
            `}
          >
            <input {...getInputProps()} />
            {isUploading ? (
              <div className="w-8 h-8 animate-spin rounded-full border-3 border-white border-t-transparent" />
            ) : (
              <CameraIcon className={`
                w-8 h-8 text-white transition-all duration-300 ease-out
                ${isDragActive || 'group-hover:opacity-100 group-hover:scale-110'} 
                ${isDragActive ? 'opacity-100 scale-110' : 'opacity-0'}
              `} />
            )}
          </div>
        </div>

        {/* Enhanced Upload Instructions */}
        <div className="mt-6 text-center">
          <div
            {...getRootProps()}
            className={`
              cursor-pointer transition-all duration-200 min-h-[48px] flex items-center justify-center px-6 py-3 rounded-xl font-medium
              ${isDarkMode 
                ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-800 border border-gray-700 hover:border-gray-600' 
                : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
              }
              ${isUploading ? 'cursor-not-allowed opacity-50' : 'hover:shadow-md'}
              ${isDragActive ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : ''}
              focus:outline-none focus:ring-4 focus:ring-purple-500 focus:ring-opacity-50
            `}
            role="button"
            tabIndex={0}
            aria-label={profilePicture ? 'Change profile photo' : 'Add profile photo'}
            aria-describedby="upload-instructions upload-requirements"
          >
            <input {...getInputProps()} />
            <CameraIcon className="w-5 h-5 mr-3" />
            <span className="text-base">
              {profilePicture ? 'Change Photo' : 'Add Photo'}
            </span>
          </div>
          
          {/* Dynamic upload guidance */}
          <div id="upload-instructions">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-3`}>
              {isDragActive 
                ? 'Drop your photo here' 
                : 'Click to upload or drag and drop'
              }
            </p>
          </div>

          {/* Always visible format requirements */}
          <div id="upload-requirements" className="mt-2">
            <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} font-medium`}>
              Supported formats: JPEG, PNG, GIF, WebP
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Maximum size: {FileValidationService.formatFileSize(FileValidationService.MAX_FILE_SIZE)}
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Recommended: 400x400 pixels or larger
            </p>
          </div>
        </div>



        {/* Enhanced Validation Messages */}
        {validationErrors.length > 0 && (
          <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border border-red-800/50' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-start">
              <ExclamationTriangleIcon className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
              <div className="flex-1">
                {validationErrors.map((error, index) => (
                  <div key={index} className="mb-2 last:mb-0">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                      {error.message}
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-red-300' : 'text-red-500'}`}>
                      {error.actionableAdvice}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Upload Error (from parent component) */}
        {uploadError && (
          <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border border-red-800/50' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-start">
              <ExclamationTriangleIcon className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
              <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                {uploadError}
              </p>
            </div>
          </div>
        )}

        {/* Validation Warnings */}
        {validationWarnings.length > 0 && (
          <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-800/50' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className="flex items-start">
              <InformationCircleIcon className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <div className="flex-1">
                {validationWarnings.map((warning, index) => (
                  <div key={index} className="mb-2 last:mb-0">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                      {warning.message}
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>
                      {warning.actionableAdvice}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cropping Modal */}
      {showCropper && fileToProcess && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={handleCropCancel} // Click outside to close
        >
          <div 
            className={`
              max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-xl relative shadow-2xl
              ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}
              mx-2 sm:mx-0 transform transition-all duration-200 ease-out
            `}
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            {/* Close Button */}
            <button
              onClick={handleCropCancel}
              className={`absolute top-3 right-3 z-10 p-2 rounded-full transition-all duration-200 ${
                isDarkMode
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200 hover:scale-110'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700 hover:scale-110'
              } focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50`}
              aria-label="Close modal"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            
            <div className="p-4 sm:p-6 pt-12 sm:pt-16">
              <ProfilePhotoCropper
                imageFile={fileToProcess}
                onCropComplete={handleCropComplete}
                onCancel={handleCropCancel}
                isModal={true}
                showCancelButton={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePictureSection;